/**
 * Main Kinematics Interface for Universal Robots
 *
 * Provides a unified API for forward and inverse kinematics
 */

import { getRobotParameters, getAvailableRobots } from './URRobotParameters.js';
import { forwardKinematics, axisAngleToRotationMatrix } from './URForwardKinematics.js';
import { inverseKinematics, pickBestSolution, isWithinLimits } from './URInverseKinematics.js';

export { getAvailableRobots };

/**
 * Multiply two 4x4 matrices
 */
function multiplyMatrices(a, b) {
  const result = Array(4).fill(0).map(() => Array(4).fill(0));
  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 4; j++) {
      for (let k = 0; k < 4; k++) {
        result[i][j] += a[i][k] * b[k][j];
      }
    }
  }
  return result;
}

/**
 * Invert a 4x4 homogeneous transformation matrix
 */
function invertTransform(T) {
  // For a transformation matrix T = [R t; 0 1]
  // The inverse is T^-1 = [R' -R't; 0 1]
  const R_transpose = [
    [T[0][0], T[1][0], T[2][0]],
    [T[0][1], T[1][1], T[2][1]],
    [T[0][2], T[1][2], T[2][2]],
  ];

  const t = [T[0][3], T[1][3], T[2][3]];
  const minus_R_transpose_t = [
    -(R_transpose[0][0] * t[0] + R_transpose[0][1] * t[1] + R_transpose[0][2] * t[2]),
    -(R_transpose[1][0] * t[0] + R_transpose[1][1] * t[1] + R_transpose[1][2] * t[2]),
    -(R_transpose[2][0] * t[0] + R_transpose[2][1] * t[1] + R_transpose[2][2] * t[2]),
  ];

  return [
    [R_transpose[0][0], R_transpose[0][1], R_transpose[0][2], minus_R_transpose_t[0]],
    [R_transpose[1][0], R_transpose[1][1], R_transpose[1][2], minus_R_transpose_t[1]],
    [R_transpose[2][0], R_transpose[2][1], R_transpose[2][2], minus_R_transpose_t[2]],
    [0, 0, 0, 1],
  ];
}

/**
 * Convert rotation matrix to axis-angle
 */
function rotationMatrixToAxisAngle(R) {
  const trace = R[0][0] + R[1][1] + R[2][2];
  const angle = Math.acos(Math.max(-1, Math.min(1, (trace - 1) / 2)));

  if (Math.abs(angle) < 1e-10) {
    return { rx: 0, ry: 0, rz: 0 };
  }

  const k = 1 / (2 * Math.sin(angle));
  const axis = [
    k * (R[2][1] - R[1][2]),
    k * (R[0][2] - R[2][0]),
    k * (R[1][0] - R[0][1]),
  ];

  return {
    rx: axis[0] * angle,
    ry: axis[1] * angle,
    rz: axis[2] * angle,
  };
}

/**
 * Remove TCP offset from a pose to get flange pose
 */
function removeTCPOffset(tcpPose, tcpOffset) {
  if (!tcpOffset) return tcpPose;

  // Create transformation matrix for TCP pose
  const R_tcp_pose = axisAngleToRotationMatrix(tcpPose.rotation.rx, tcpPose.rotation.ry, tcpPose.rotation.rz);
  const T_tcp_pose = [
    [R_tcp_pose[0][0], R_tcp_pose[0][1], R_tcp_pose[0][2], tcpPose.position.x],
    [R_tcp_pose[1][0], R_tcp_pose[1][1], R_tcp_pose[1][2], tcpPose.position.y],
    [R_tcp_pose[2][0], R_tcp_pose[2][1], R_tcp_pose[2][2], tcpPose.position.z],
    [0, 0, 0, 1],
  ];

  // Create transformation matrix for TCP offset
  const R_tcp_offset = axisAngleToRotationMatrix(tcpOffset.rx, tcpOffset.ry, tcpOffset.rz);
  const T_tcp_offset = [
    [R_tcp_offset[0][0], R_tcp_offset[0][1], R_tcp_offset[0][2], tcpOffset.x],
    [R_tcp_offset[1][0], R_tcp_offset[1][1], R_tcp_offset[1][2], tcpOffset.y],
    [R_tcp_offset[2][0], R_tcp_offset[2][1], R_tcp_offset[2][2], tcpOffset.z],
    [0, 0, 0, 1],
  ];

  // Compute flange pose: T_flange = T_tcp_pose * T_tcp_offset^-1
  const T_tcp_offset_inv = invertTransform(T_tcp_offset);
  const T_flange = multiplyMatrices(T_tcp_pose, T_tcp_offset_inv);

  // Extract position and rotation
  const flangePose = {
    position: {
      x: T_flange[0][3],
      y: T_flange[1][3],
      z: T_flange[2][3],
    },
    rotation: rotationMatrixToAxisAngle([
      [T_flange[0][0], T_flange[0][1], T_flange[0][2]],
      [T_flange[1][0], T_flange[1][1], T_flange[1][2]],
      [T_flange[2][0], T_flange[2][1], T_flange[2][2]],
    ]),
  };

  return flangePose;
}

/**
 * UR Robot Kinematics Solver
 */
export class URKinematics {
  /**
   * Create a new kinematics solver for a specific robot model
   * @param {string} robotModel - Robot model name (e.g., 'UR10e', 'UR20')
   */
  constructor(robotModel) {
    this.robot = getRobotParameters(robotModel);
    this.robotModel = robotModel;
  }

  /**
   * Compute forward kinematics
   * @param {Array<number>} joints - Joint angles in radians [q1, q2, q3, q4, q5, q6]
   * @returns {Object} End-effector pose { position: {x, y, z}, rotation: {rx, ry, rz} }
   */
  computeFK(joints) {
    return forwardKinematics(joints, this.robot.dh, this.robot.defaultTCP);
  }

  /**
   * Compute inverse kinematics and return all possible solutions
   * @param {Object} targetPose - Target TCP pose
   * @param {Object} targetPose.position - Position {x, y, z} in meters
   * @param {Object} targetPose.rotation - Rotation {rx, ry, rz} in radians (axis-angle)
   * @param {Array<number>} initialJoints - Initial joint guess (optional, used as starting point)
   * @returns {Array<Array<number>>} Array of joint angle solutions (up to 8)
   */
  computeIKAll(targetPose, initialJoints = null) {
    // Remove TCP offset to get flange pose
    const flangePose = removeTCPOffset(targetPose, this.robot.defaultTCP);
    return inverseKinematics(flangePose, this.robot.dh, initialJoints);
  }

  /**
   * Compute inverse kinematics and return the best solution
   * @param {Object} targetPose - Target TCP pose
   * @param {Object} targetPose.position - Position {x, y, z} in meters
   * @param {Object} targetPose.rotation - Rotation {rx, ry, rz} in radians (axis-angle)
   * @param {Array<number>} currentJoints - Current joint angles (optional, used as initial guess and for picking best solution)
   * @returns {Array<number>|null} Best joint angle solution, or null if no solution found
   */
  computeIK(targetPose, currentJoints = null) {
    const solutions = this.computeIKAll(targetPose, currentJoints);

    if (solutions.length === 0) {
      return null;
    }

    // Filter solutions within joint limits
    const validSolutions = solutions.filter(sol => isWithinLimits(sol, this.robot.jointLimits));

    if (validSolutions.length === 0) {
      return null;
    }

    // Pick best solution based on current joints
    if (currentJoints && currentJoints.length === 6) {
      return pickBestSolution(validSolutions, currentJoints);
    }

    // If no current joints provided, return first valid solution
    return validSolutions[0];
  }

  /**
   * Verify an IK solution by computing FK and comparing with target
   * @param {Array<number>} joints - Joint angles to verify
   * @param {Object} targetPose - Target pose to compare against
   * @returns {Object} Verification result { valid: boolean, posError: number, rotError: number }
   */
  verifyIKSolution(joints, targetPose) {
    const fkResult = this.computeFK(joints);

    // Calculate position error (Euclidean distance)
    const posError = Math.sqrt(
      Math.pow(fkResult.position.x - targetPose.position.x, 2) +
        Math.pow(fkResult.position.y - targetPose.position.y, 2) +
        Math.pow(fkResult.position.z - targetPose.position.z, 2)
    );

    // Calculate rotation error (axis-angle difference)
    const rotError = Math.sqrt(
      Math.pow(fkResult.rotation.rx - targetPose.rotation.rx, 2) +
        Math.pow(fkResult.rotation.ry - targetPose.rotation.ry, 2) +
        Math.pow(fkResult.rotation.rz - targetPose.rotation.rz, 2)
    );

    // Thresholds: 1mm for position, 0.01 rad (~0.57°) for rotation
    const valid = posError < 0.001 && rotError < 0.01;

    return {
      valid,
      posError,
      rotError,
      fkPose: fkResult,
    };
  }

  /**
   * Get robot specifications
   * @returns {Object} Robot parameters
   */
  getRobotInfo() {
    return {
      model: this.robotModel,
      name: this.robot.name,
      description: this.robot.description,
      reach: this.robot.reach,
      payload: this.robot.payload,
      jointLimits: this.robot.jointLimits,
    };
  }

  /**
   * Check if a target pose is reachable (has at least one IK solution)
   * @param {Object} targetPose - Target pose
   * @returns {boolean} True if reachable
   */
  isReachable(targetPose) {
    const solutions = this.computeIKAll(targetPose);
    const validSolutions = solutions.filter(sol => isWithinLimits(sol, this.robot.jointLimits));
    return validSolutions.length > 0;
  }

  /**
   * Get the number of valid IK solutions for a target pose
   * @param {Object} targetPose - Target pose
   * @returns {number} Number of valid solutions (0-8)
   */
  getSolutionCount(targetPose) {
    const solutions = this.computeIKAll(targetPose);
    const validSolutions = solutions.filter(sol => isWithinLimits(sol, this.robot.jointLimits));
    return validSolutions.length;
  }
}

/**
 * Create a kinematics solver for a specific robot
 * @param {string} robotModel - Robot model name
 * @returns {URKinematics} Kinematics solver instance
 */
export function createKinematicsSolver(robotModel) {
  return new URKinematics(robotModel);
}
